import { useQuery } from '@tanstack/react-query'
import { X, BookOpen, Calendar, ChevronRight, Loader2, FileQuestion } from 'lucide-react'
import { api } from '../../lib/api'

interface ExpectedAssignment {
  id: number
  source_type: string  // "calendar_event" | "assignment" | "agenda_homework"
  lesson_number: number | null
  name: string
  subject: string | null
  course_name: string | null
  due_at: string | null
  priority: number
}

interface AssignmentSelectorProps {
  onSelect: (assignment: ExpectedAssignment | null) => void  // null = "Other"
  onClose: () => void
}

export function AssignmentSelector({ onSelect, onClose }: AssignmentSelectorProps) {
  const { data, isLoading, error } = useQuery<{ assignments: ExpectedAssignment[] }>({
    queryKey: ['expected-assignments'],
    queryFn: async () => {
      const response = await api.get('/api/student/scan/expected-assignments')
      return response.data
    },
  })

  const assignments = data?.assignments || []

  // Group by subject
  const grouped = assignments.reduce((acc, assignment) => {
    const subject = assignment.subject || 'Other'
    if (!acc[subject]) acc[subject] = []
    acc[subject].push(assignment)
    return acc
  }, {} as Record<string, ExpectedAssignment[]>)

  const formatDueDate = (dateStr: string | null) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    const now = new Date()
    const diffDays = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Due today'
    if (diffDays === -1) return 'Due yesterday'
    if (diffDays === 1) return 'Due tomorrow'
    if (diffDays < 0) return `Due ${Math.abs(diffDays)} days ago`
    return `Due in ${diffDays} days`
  }

  const subjectColors: Record<string, string> = {
    Math: 'bg-blue-500/20 text-blue-400',
    Reading: 'bg-green-500/20 text-green-400',
    Writing: 'bg-purple-500/20 text-purple-400',
    Science: 'bg-yellow-500/20 text-yellow-400',
    History: 'bg-orange-500/20 text-orange-400',
    Other: 'bg-gray-500/20 text-gray-400',
  }

  return (
    <div className="fixed inset-0 z-50 bg-surface-base/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-surface-card border-b border-gray-700/50">
        <div>
          <h2 className="text-lg font-semibold">What are you scanning?</h2>
          <p className="text-sm text-gray-400">Select the assignment you completed</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-quest-primary animate-spin" />
          </div>
        )}

        {error && (
          <div className="card text-center py-8">
            <p className="text-red-400">Failed to load assignments</p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* Expected assignments by subject */}
            {Object.entries(grouped).map(([subject, items]) => (
              <div key={subject} className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <BookOpen className="w-4 h-4" />
                  <span>{subject}</span>
                </div>
                <div className="space-y-2">
                  {items.map((assignment) => (
                    <button
                      key={assignment.id}
                      onClick={() => onSelect(assignment)}
                      className="w-full card p-4 hover:bg-surface-hover transition-colors flex items-center justify-between text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{assignment.name}</p>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                          {assignment.course_name && (
                            <span className="truncate">{assignment.course_name}</span>
                          )}
                          {assignment.due_at && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDueDate(assignment.due_at)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${subjectColors[subject] || subjectColors['Other']}`}>
                          {subject}
                        </span>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {assignments.length === 0 && (
              <div className="card text-center py-8">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                <p className="text-gray-400">No expected assignments found</p>
                <p className="text-sm text-gray-500 mt-1">
                  Try "Scan Other" to capture homework anyway
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* "Other" option - always visible at bottom */}
      <div className="p-4 bg-surface-card border-t border-gray-700/50">
        <button
          onClick={() => onSelect(null)}
          className="w-full card p-4 border-dashed border-gray-600 hover:border-gray-500 hover:bg-surface-hover/50 transition-colors flex items-center justify-center gap-3"
        >
          <FileQuestion className="w-6 h-6 text-gray-400" />
          <div className="text-left">
            <p className="font-medium">Scan Other</p>
            <p className="text-sm text-gray-400">Not in the list above? Scan anyway</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
        </button>
      </div>
    </div>
  )
}
